import { ColorPrimitives, type MaterialSymbolType } from '@front/model'
import { type PermGraphNode, type PermGraphNodeKind } from './types'

export type PermGraphAccessTone = 'disabled' | 'enabled'

export type PermGraphBorderRole = 'applied' | 'effective' | 'source'

export type PermGraphNodeTheme = {
  background: string
  border: string
  borderWidth: number
  pillBackground: string
  text: string
}

export type PermGraphConnectorTheme = {
  color: string
  width: number
}

export const PERM_GRAPH_RING_COLOR = `light-dark(${ColorPrimitives.blue['900']}, ${ColorPrimitives.blue['400']})`

export const PERM_GRAPH_LABEL_BACKGROUND = `light-dark(${ColorPrimitives.blue['900']}, ${ColorPrimitives.blue['700']})`

export const PERM_GRAPH_LABEL_COLOR = ColorPrimitives.neutral['50']

export const PERM_GRAPH_WINNER_LABEL = 'Takes effect'

export const PERM_GRAPH_LINE_COLORS = {
  applied: PERM_GRAPH_RING_COLOR,
  disabled: ColorPrimitives.rose['500'],
  enabled: ColorPrimitives.emerald['500'],
}

const TONES: Record<PermGraphAccessTone, Omit<PermGraphNodeTheme, 'borderWidth'>> = {
  enabled: {
    background: `light-dark(${ColorPrimitives.emerald['50']}, color-mix(in srgb, ${ColorPrimitives.emerald['900']} 20%, var(--mantine-color-dark-8)))`,
    border: ColorPrimitives.emerald['500'],
    pillBackground: `light-dark(color-mix(in srgb, ${ColorPrimitives.emerald['800']} 10%, transparent), color-mix(in srgb, ${ColorPrimitives.emerald['200']} 18%, transparent))`,
    text: `light-dark(${ColorPrimitives.emerald['800']}, ${ColorPrimitives.emerald['200']})`,
  },
  disabled: {
    background: `light-dark(${ColorPrimitives.rose['50']}, color-mix(in srgb, ${ColorPrimitives.rose['900']} 20%, var(--mantine-color-dark-8)))`,
    border: ColorPrimitives.rose['500'],
    pillBackground: `light-dark(color-mix(in srgb, ${ColorPrimitives.rose['900']} 10%, transparent), color-mix(in srgb, ${ColorPrimitives.rose['200']} 18%, transparent))`,
    text: `light-dark(${ColorPrimitives.rose['900']}, ${ColorPrimitives.rose['200']})`,
  },
}

const BORDER_ROLES: Record<PermGraphBorderRole, { borderWidth: number; useRing: boolean }> = {
  applied: { borderWidth: 3, useRing: true },
  effective: { borderWidth: 1, useRing: true },
  source: { borderWidth: 1, useRing: false },
}

export function getPermGraphAccessTone(node: PermGraphNode): PermGraphAccessTone {
  return node.flowScope !== undefined && node.flowScope !== 'none' ? 'enabled' : 'disabled'
}

export function getPermGraphBorderRole(node: PermGraphNode): PermGraphBorderRole {
  if (node.isAppliedSource) return 'applied'
  if (node.isTarget) return 'effective'
  return 'source'
}

export function getPermGraphNodeTheme(node: PermGraphNode): PermGraphNodeTheme {
  const tone = TONES[getPermGraphAccessTone(node)]
  const role = BORDER_ROLES[getPermGraphBorderRole(node)]
  return { ...tone, border: role.useRing ? PERM_GRAPH_RING_COLOR : tone.border, borderWidth: role.borderWidth }
}

export function getPermGraphConnectorTheme(node: PermGraphNode, isWinningPath: boolean): PermGraphConnectorTheme {
  if (isWinningPath) return { color: PERM_GRAPH_LINE_COLORS.applied, width: 3 }
  return { color: PERM_GRAPH_LINE_COLORS[getPermGraphAccessTone(node)], width: 3 }
}

const KIND_ICONS: Record<PermGraphNodeKind, MaterialSymbolType> = {
  company: 'business',
  defaultRole: 'badge',
  role: 'badge',
  team: 'groups',
  user: 'person',
}

export function getPermGraphNodeIcon(kind: PermGraphNodeKind): MaterialSymbolType {
  return KIND_ICONS[kind]
}

function toTargetNoun(kind: PermGraphNodeKind): string {
  switch (kind) {
    case 'company':
      return 'company'
    case 'team':
      return 'team'
    case 'defaultRole':
    case 'role':
      return 'role'
    case 'user':
      return 'user'
  }
}

export function getPermGraphNodeStateLabel(node: PermGraphNode): string {
  const access = getPermGraphAccessTone(node) === 'enabled' ? 'grants access' : 'denies access'
  switch (getPermGraphBorderRole(node)) {
    case 'applied':
      return `${PERM_GRAPH_WINNER_LABEL} - winning source, ${access}`
    case 'effective':
      return `Result for this ${toTargetNoun(node.kind)}`
    case 'source':
      return `Source - ${access}`
  }
}
